import type { CSSProperties } from "react";
import {
  IconBell,
  IconCal,
  IconCog,
  IconDoor,
  IconGrid,
  IconLink,
  IconPhone,
  IconSearch,
  IconSpark,
} from "../atoms/icons";

type RoomState = "oc" | "clean" | "dirty" | "out";

/**
 * 42-room mix across statuses for the dashboard's room-status card.
 * Room numbers walk 101, 102, …, 110, 201, 202, …  etc so each row of 10
 * rooms maps to a floor.
 */
function generateRooms(): { n: number; s: RoomState }[] {
  const states: RoomState[] = [
    "oc", "oc", "clean", "oc", "dirty", "clean", "oc", "clean", "oc", "dirty",
    "oc", "clean", "oc", "oc", "dirty", "clean", "oc", "out", "clean", "oc",
    "dirty", "oc", "clean", "oc", "oc", "clean", "dirty", "oc", "clean", "oc",
    "oc", "out", "clean", "dirty", "oc", "clean", "oc", "clean", "oc", "oc",
    "clean", "dirty",
  ];
  return states.map((s, i) => ({
    n: 101 + i + Math.floor(i / 10) * 100,
    s,
  }));
}

/**
 * Live-looking PMS dashboard that sits below the hero copy. Contains a mock
 * browser chrome, sidebar navigation, greeting row with segmented control,
 * 4 KPI cards (Belegung / ADR / RevPAR / Fonio AI Quote), arrivals list,
 * 42-room status grid, Fonio live feed with waveform, and an automations
 * queue strip. Two floating chips are absolutely positioned on desktop
 * and hidden on mobile via CSS.
 */
export function HeroVisual() {
  return (
    <div className="hero-visual">
      <div className="dash">
        {/* Mock browser chrome */}
        <div className="dash-chrome">
          <span className="hv-dot" />
          <span className="hv-dot" />
          <span className="hv-dot" />
          <span className="hv-url">app.stratex-ai.com / dashboard</span>
          <span className="dash-chrome-right">
            <span className="chip live">
              <span className="dot-live" /> Live
            </span>
          </span>
        </div>

        <div className="dash-body">
          {/* Sidebar */}
          <aside className="dash-sb">
            <div className="dash-logo">
              stratex<span>.ai</span>
            </div>
            <nav className="dash-nav">
              <a className="active">
                <IconGrid /> Overview
              </a>
              <a>
                <IconCal /> Reservierungen
              </a>
              <a>
                <IconDoor /> Zimmer <span className="nav-badge">214</span>
              </a>
              <a>
                <IconPhone /> KOVA AI <span className="nav-badge accent">12</span>
              </a>
              <a>
                <IconSpark /> Revenue
              </a>
              <a>
                <IconLink /> Integrationen
              </a>
              <a>
                <IconCog /> Einstellungen
              </a>
            </nav>
            <div className="dash-sb-foot">
              <div className="avatar">MS</div>
              <div>
                <div className="sb-name">Marie Schneider</div>
                <div className="sb-role">Haus Alpenblick</div>
              </div>
            </div>
          </aside>

          {/* Main content — scrolls internally when taller than the 16:9 body */}
          <div className="dash-main">
            <div className="dash-top">
              <div>
                <div className="dash-breadcrumb">Overview · Heute · Mo 4. März</div>
                <div className="dash-h1">
                  Guten Morgen, Marie <span className="wave">👋</span>
                </div>
              </div>
              <div className="dash-top-r">
                <div className="seg">
                  <button className="active">Heute</button>
                  <button>Woche</button>
                  <button>Monat</button>
                </div>
                <button className="btn-i" aria-label="Benachrichtigungen">
                  <IconBell />
                  <span className="badge-dot" />
                </button>
                <button className="btn-i" aria-label="Suche">
                  <IconSearch />
                </button>
              </div>
            </div>

            {/* KPI row — 2×2 grid at default, stacks on mobile */}
            <div className="kpi-row">
              <div className="kpi">
                <div className="kpi-k">Belegung</div>
                <div className="kpi-v">
                  87<span>%</span>
                </div>
                <div className="kpi-sub up">↑ 4.2% vs. LW</div>
                <svg className="kpi-bars" viewBox="0 0 80 24" preserveAspectRatio="none">
                  {[14, 10, 16, 12, 18, 15, 20, 17, 22, 19, 21, 23].map((h, i) => (
                    <rect
                      key={i}
                      x={i * 7}
                      y={24 - h}
                      width="4"
                      height={h}
                      rx="1"
                      fill="currentColor"
                      opacity={0.25 + i * 0.06}
                    />
                  ))}
                </svg>
              </div>
              <div className="kpi">
                <div className="kpi-k">ADR</div>
                <div className="kpi-v">
                  218<span>€</span>
                </div>
                <div className="kpi-sub up">↑ 11€</div>
                <svg className="spark-lg" viewBox="0 0 80 30" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="sg1" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="currentColor" stopOpacity="0.25" />
                      <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,22 L8,20 L16,18 L24,19 L32,14 L40,16 L48,10 L56,12 L64,8 L72,6 L80,9 L80,30 L0,30 Z"
                    fill="url(#sg1)"
                  />
                  <polyline
                    points="0,22 8,20 16,18 24,19 32,14 40,16 48,10 56,12 64,8 72,6 80,9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <div className="kpi">
                <div className="kpi-k">RevPAR</div>
                <div className="kpi-v">
                  189<span>€</span>
                </div>
                <div className="kpi-sub up">↑ 7.1%</div>
                <svg className="spark-lg" viewBox="0 0 80 30" preserveAspectRatio="none">
                  <polyline
                    points="0,20 8,18 16,19 24,14 32,16 40,12 48,11 56,9 64,10 72,6 80,7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <div className="kpi">
                <div className="kpi-k">KOVA AI Quote</div>
                <div className="kpi-with-ring">
                  <div className="kpi-v accent">
                    84<span>%</span>
                  </div>
                  <div className="ring-mini">
                    <svg viewBox="0 0 40 40">
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        opacity="0.15"
                      />
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeDasharray="100.5"
                        strokeDashoffset="16"
                        strokeLinecap="round"
                        transform="rotate(-90 20 20)"
                      />
                    </svg>
                  </div>
                </div>
                <div className="kpi-sub">28 von 34 Calls</div>
              </div>
            </div>

            {/* Dashboard content grid — arrivals and automations span both columns */}
            <div className="dash-grid">
              {/* Arrivals */}
              <div className="card card-arr">
                <div className="card-h">
                  <div className="card-h-l">
                    <span className="card-t">Ankünfte heute</span>
                    <span className="card-cnt">8 · davon 3 bestätigt</span>
                  </div>
                  <button className="link-btn">Alle →</button>
                </div>
                <div className="arr-list">
                  <ArrivalRow
                    initials="FW"
                    color="oklch(0.85 0.09 260)"
                    name="Familie Weber"
                    meta="Deluxe Suite · 3 Nächte"
                    time="16:30"
                    tag="Check-in bereit"
                    tagClass="tag-ok"
                  />
                  <ArrivalRow
                    initials="LR"
                    color="oklch(0.85 0.08 140)"
                    name="Lukas Rossi"
                    meta="Standard · 1 Nacht"
                    time="17:45"
                    tag="Später angekündigt"
                    tagClass="tag-warn"
                  />
                  <ArrivalRow
                    initials="KM"
                    color="oklch(0.85 0.07 40)"
                    name="Karin Müller"
                    meta="Junior Suite · 4 Nächte"
                    time="18:20"
                    tag="Warteschlange"
                  />
                  <ArrivalRow
                    initials="+5"
                    color="oklch(0.86 0.06 320)"
                    name="5 weitere Ankünfte"
                    meta="Gruppenbuchung Offsite"
                    time="19:00+"
                    tag="Geplant"
                  />
                </div>
              </div>

              {/* Room status */}
              <div className="card card-rooms">
                <div className="card-h">
                  <div className="card-h-l">
                    <span className="card-t">Zimmerstatus</span>
                    <span className="card-cnt">42 Zimmer · Stock 1–4</span>
                  </div>
                  <div className="room-legend">
                    <span>
                      <i className="s-clean" /> Bereit
                    </span>
                    <span>
                      <i className="s-dirty" /> Reinigung
                    </span>
                    <span>
                      <i className="s-oc" /> Belegt
                    </span>
                    <span>
                      <i className="s-out" /> Out
                    </span>
                  </div>
                </div>
                <div className="room-grid">
                  {generateRooms().map((r, i) => (
                    <div key={i} className={`room room-${r.s}`} title={`${r.n} · ${r.s}`}>
                      <span>{r.n}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fonio live feed */}
              <div className="card card-feed">
                <div className="card-h">
                  <div className="card-h-l">
                    <span className="card-t">KOVA AI · Live</span>
                    <span className="card-cnt">
                      <span className="dot-live" /> 2 aktive Calls
                    </span>
                  </div>
                  <button className="link-btn">Mithören →</button>
                </div>
                <div className="feed">
                  <div className="feed-row live">
                    <div className="feed-time">jetzt</div>
                    <div className="feed-body">
                      <div className="feed-t">+43 720 080785 beantwortet</div>
                      <div className="feed-d">
                        „Haben Sie ein Zimmer für Freitag bis Sonntag?“
                      </div>
                      <div className="feed-wave">
                        {[6, 10, 14, 8, 16, 12, 18, 9, 14, 11, 15, 7, 13, 10, 16, 8, 12].map(
                          (h, i) => (
                            <span
                              key={i}
                              style={{ height: h + "px", animationDelay: i * 0.08 + "s" }}
                            />
                          )
                        )}
                      </div>
                    </div>
                    <span className="feed-tag accent">AI · spricht</span>
                  </div>
                  <div className="feed-row">
                    <div className="feed-time">−12 s</div>
                    <div className="feed-body">
                      <div className="feed-t">Spätes Check-in bestätigt</div>
                      <div className="feed-d">Zimmer 214 · Gast informiert via SMS</div>
                    </div>
                    <span className="feed-tag ok">Automatisiert</span>
                  </div>
                  <div className="feed-row">
                    <div className="feed-time">−1 m</div>
                    <div className="feed-body">
                      <div className="feed-t">Rechnungsfrage beantwortet</div>
                      <div className="feed-d">Reservierung #4821 · PDF erneut gesendet</div>
                    </div>
                    <span className="feed-tag ok">Automatisiert</span>
                  </div>
                  <div className="feed-row muted">
                    <div className="feed-time">−4 m</div>
                    <div className="feed-body">
                      <div className="feed-t">Gruppenanfrage → Sales-Inbox</div>
                      <div className="feed-d">14 Zimmer · Mai, komplex → Mensch</div>
                    </div>
                    <span className="feed-tag">Eskaliert</span>
                  </div>
                </div>
              </div>

              {/* Automations queue */}
              <div className="card card-tasks">
                <div className="card-h">
                  <div className="card-h-l">
                    <span className="card-t">Automations-Queue</span>
                    <span className="card-cnt">16 heute ausgeführt</span>
                  </div>
                </div>
                <div className="task-strip">
                  <div className="task done">
                    <span className="t-ic">✓</span> Zimmerzuweisung
                  </div>
                  <div className="task done">
                    <span className="t-ic">✓</span> Check-in Mails
                  </div>
                  <div className="task active">
                    <span className="t-ic t-spin">◐</span> Kanalpreise sync
                  </div>
                  <div className="task">
                    <span className="t-ic">•</span> Rechnungen DATEV
                  </div>
                  <div className="task">
                    <span className="t-ic">•</span> No-Show Prüfung
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating annotation chips (desktop-only) */}
      <div className="hv-float hv-float-a">
        <div className="hv-float-title">Automatisiert · gerade eben</div>
        <div className="hv-float-body">
          Spätes Check-in bestätigt
          <br />
          <span style={{ color: "var(--ink-faint)", fontSize: "11px" }}>
            KOVA AI → Mews → Gast via SMS
          </span>
        </div>
      </div>
      <div className="hv-float hv-float-b">
        <div className="hv-float-title accent2">+37 min gespart</div>
        <div className="hv-float-body">heute am Empfang</div>
      </div>
    </div>
  );
}

type ArrivalRowProps = {
  initials: string;
  color: string;
  name: string;
  meta: string;
  time: string;
  tag: string;
  tagClass?: string;
};

function ArrivalRow({
  initials,
  color,
  name,
  meta,
  time,
  tag,
  tagClass = "",
}: ArrivalRowProps) {
  const avatarStyle: CSSProperties = { background: color };
  return (
    <div className="arr-row">
      <div className="guest">
        <div className="g-avatar" style={avatarStyle}>
          {initials}
        </div>
        <div>
          <div className="g-name">{name}</div>
          <div className="g-meta">{meta}</div>
        </div>
      </div>
      <div className="arr-meta">
        <div className="arr-time">{time}</div>
        <span className={`tag ${tagClass}`.trim()}>{tag}</span>
      </div>
    </div>
  );
}
